import { BISHOP, CASTLE_MASK, COLOR_MASK, KNIGHT, PAWN, QUEEN, ROOK, TYPE_MASK, moveFrom, movePromo } from '../board.ts';
import { WOUNDED_BISHOP, WOUNDED_KNIGHT, WOUNDED_PAWN, WOUNDED_QUEEN, WOUNDED_ROOK } from '../pieces.ts';
import type { MoveContext } from '../rules.ts';
import type { Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 7 (draft). Every piece has 2 HP. Capturing a healthy piece only wounds it: the
// attacker stays where it was and the target stays put, wounded. Capturing a wounded piece
// takes it as usual. Kings have 1 HP, so check and checkmate work normally. Wounded pieces
// move exactly like healthy ones, and a promoted pawn comes back at full health.
// No en passant (a pawn that can't be removed in one hit makes it meaningless).

/** Wounded version of each healthy piece type (0: one hit kills it). */
export const WOUNDED_OF: number[] = [];
WOUNDED_OF[PAWN] = WOUNDED_PAWN;
WOUNDED_OF[KNIGHT] = WOUNDED_KNIGHT;
WOUNDED_OF[BISHOP] = WOUNDED_BISHOP;
WOUNDED_OF[ROOK] = WOUNDED_ROOK;
WOUNDED_OF[QUEEN] = WOUNDED_QUEEN;

export function isWounded(piece: number): boolean {
  const t = piece & TYPE_MASK;
  return t >= WOUNDED_PAWN && t <= WOUNDED_QUEEN;
}

/** Whether this capture only wounds (the target was healthy and isn't a king). */
export function woundsOnly(captured: number): boolean {
  return !!WOUNDED_OF[captured & TYPE_MASK];
}

export const hp: Variant = {
  id: 'hp',
  info: {
    name: 'HP Chess',
    but: 'every piece has 2 HP',
    tagline: 'It takes two hits to take a piece. The first one just hurts.',
    summary: 'Every piece has **2 HP**. The first capture only wounds it, and the attacker stays put. Hit it again to take it. Kings have 1 HP.',
    rules: [
      'Every piece except the king has 2 HP.',
      'Capturing a healthy piece wounds it instead: it stays on its square and your piece stays on its own.',
      'Capturing a wounded piece takes it off the board as normal.',
      'Kings have 1 HP, so check and checkmate work as usual. A pawn that promotes comes back at full health.',
      'No en passant. Stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#ff5d8f',
    emoji: '❤️',
    bots: ['Scratch', 'Bruiser', 'Tank', 'Juggernaut', 'Immortal'],
  },
  startFen: STANDARD_FEN,
  extraPieces: [WOUNDED_PAWN, WOUNDED_KNIGHT, WOUNDED_BISHOP, WOUNDED_ROOK, WOUNDED_QUEEN],
  enPassant: false,
  // Extra hit points don't help a lone minor piece mate.
  insufficientMaterial: true,

  afterMove(ctx: MoveContext) {
    const { captured, capturedSq, next } = ctx;
    if (!captured || !woundsOnly(captured)) return;
    // The attacker never left, and the target is still there, one hit down.
    const from = moveFrom(ctx.move);
    const wounded = WOUNDED_OF[captured & TYPE_MASK] | (captured & COLOR_MASK);
    next.board[from] = ctx.piece;
    next.board[capturedSq] = wounded;
    if (ctx.rules.isRoyal[ctx.piece & TYPE_MASK]) next.royals[ctx.us] = from;
    // Nothing moved, so the attacker keeps its castling rights (the target's are gone).
    next.castling = ctx.prev.castling & CASTLE_MASK[capturedSq];
    if (ctx.events) {
      ctx.events.length = 0;
      ctx.events.push({ t: 'fx', kind: 'hit', sq: capturedSq });
      ctx.events.push({ t: 'add', sq: capturedSq, piece: wounded, cause: 'wound' });
    }
  },

  isLegal(ctx: MoveContext) {
    // A pawn that only wounds doesn't promote, so its four promotion moves are the same
    // move: keep just the first.
    const promo = movePromo(ctx.move);
    return !promo || !woundsOnly(ctx.captured) || promo === ctx.rules.promotions[0];
  },
};
